import { Fragment, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import type { SortAccessors } from '@/lib/tableSort'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { ColumnCells, ResizableTableHead, ResizableTh } from '@/components/table'
import { ChevronDown, ChevronRight, Pencil, Plus, Thermometer, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table'
import { ProfileFieldsModal } from './ProfileFieldsModal'
import { formatFieldValue, hasDefault, resolveProfileFields } from '@/lib/profileFields'
import { useConditionOptionLabel } from './storage/ConditionControls'
import { useDeleteProfile, useFieldDefs, useProfiles } from '@/hooks/useInventoryMaster'
import { usePermission } from '@/lib/permissions'
import { storageTemperaturesHref } from '@/lib/storageRoutes'
import type { ApiProfile, InventoryProfileType } from '@/types/inventory'

/** A category's profiles as a table: expand a row for enabled fields; Edit opens the modal. */
// Per-user column layout for the profile list, plus the nested field table an
// expanded profile shows. Two tables, two ids: they share nothing but the fact
// that one opens inside the other.
// See src/components/table/index.ts for the pattern.
const PROFILES_TABLE_ID = 'inventory-profiles'

const PROFILES_COLUMNS: TableColumnDef[] = [
  { id: 'expand', header: 'Fields', locked: true },
  { id: 'name', header: 'Name', locked: true, sortable: true },
  { id: 'fieldsYes', header: 'Fields YES', sortable: true },
  { id: 'withDefault', header: 'With a default', sortable: true },
  { id: 'actions', header: 'Actions', locked: true },
]

const PROFILE_HEAD_CLASSES: Record<string, string | undefined> = {
  expand: 'w-8',
  actions: 'text-right',
}

const PROFILE_FIELDS_TABLE_ID = 'inventory-profile-fields'

const PROFILE_FIELDS_COLUMNS: TableColumnDef[] = [
  { id: 'fieldName', header: 'Field Name', locked: true },
  { id: 'description', header: 'Description' },
  { id: 'default', header: 'Default' },
]

export function ProfilesConfig({ profileType }: { profileType: InventoryProfileType }) {
  const cols = useTableColumns(PROFILES_TABLE_ID, PROFILES_COLUMNS)
  const fieldCols = useTableColumns(PROFILE_FIELDS_TABLE_ID, PROFILE_FIELDS_COLUMNS)
  const { can } = usePermission()
  const canConfigure = can('inventory.configure')
  const { data: profiles = [] } = useProfiles(profileType)
  const { data: defs = [] } = useFieldDefs(profileType)
  const deleteProfile = useDeleteProfile()

  const [expanded, setExpanded] = useState<string | null>(null)
  // null = modal closed; { profile: null } = create; { profile } = edit
  const [modal, setModal] = useState<{ profile: ApiProfile | null } | null>(null)
  const navigate = useNavigate()

  const descByKey = new Map(defs.map((d) => [d.key, d.description]))
  // Storage conditions are stored as enum values so put-away can compare them;
  // this reads them back as words. A pass-through for every other enum field.
  const conditionLabel = useConditionOptionLabel()
  const enabledFields = (p: ApiProfile) => resolveProfileFields(defs, p.fields).filter((f) => f.active)
  // Header sorting (z8nrz7e4h6). The counts come from the field defs, so the
  // accessors are built here. The nested field table keeps the defs' order.
  const sortAccessors = useMemo<SortAccessors<ApiProfile>>(() => {
    const yes = (p: ApiProfile) => resolveProfileFields(defs, p.fields).filter((f) => f.active)
    return {
      name: p => p.name,
      fieldsYes: p => yes(p).length,
      withDefault: p => yes(p).filter(hasDefault).length,
    }
  }, [defs])
  const sortedProfiles = useSortedRows(profiles, cols.sort, sortAccessors)

  return (
    <div className='rounded-2xl border border-gray-200 bg-white shadow-sm'>
      <div className='flex items-center justify-between border-b border-gray-100 px-5 py-4'>
        <div>
          <h3 className='text-sm font-semibold text-gray-900'>Profiles</h3>
          <p className='text-xs text-gray-500'>
            {profiles.length} profile{profiles.length === 1 ? '' : 's'} · expand a row to see its enabled fields
          </p>
        </div>
        <div className='flex items-center gap-2'>
          {/* The list a storage profile's condition is picked from. */}
          {profileType === 'storage' && (
            <Button size='sm' variant='outline' className='gap-1' onClick={() => navigate(storageTemperaturesHref())}>
              <Thermometer className='h-4 w-4' />
              Temperatures
            </Button>
          )}
          {canConfigure && (
            <Button size='sm' className='gap-1' onClick={() => setModal({ profile: null })}>
              <Plus className='h-4 w-4' />
              Add profile
            </Button>
          )}
        </div>
      </div>

      {/* "With a default" is there because a profile says which fields apply AND
          what they hand down — the second is what actually reaches a material,
          so it belongs in the list rather than only inside the editor. */}
      <Table>
        <TableHeader>
          <TableRow>
            {cols.visible.map(({ def }) => (
              <ResizableTableHead
                key={def.id}
                columnId={def.id}
                columns={cols}
                label={def.header}
                className={PROFILE_HEAD_CLASSES[def.id]}
                resizable={def.id !== 'expand'}
              >
                {def.id === 'expand' ? null : def.header}
              </ResizableTableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedProfiles.map((p) => {
            const yes = enabledFields(p)
            const withDefault = yes.filter(hasDefault)
            const isOpen = expanded === p.id
            return (
              <Fragment key={p.id}>
                <TableRow className='cursor-pointer' onClick={() => setExpanded(isOpen ? null : p.id)}>
                  <ColumnCells
                    columns={cols}
                    cells={{
                      expand: (
                        <TableCell>
                          {isOpen ? (
                            <ChevronDown className='h-4 w-4 text-gray-400' />
                          ) : (
                            <ChevronRight className='h-4 w-4 text-gray-400' />
                          )}
                        </TableCell>
                      ),
                      name: (
                        <TableCell className='font-medium text-gray-800'>
                          {p.name}
                          {p.isDefault && <span className='ml-2 text-xs text-gray-400'>(default)</span>}
                        </TableCell>
                      ),
                      fieldsYes: (
                        <TableCell>
                          <span className='rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-600'>
                            {yes.length}
                          </span>
                        </TableCell>
                      ),
                      withDefault: (
                        <TableCell>
                          {withDefault.length === 0 ? (
                            <span className='text-xs text-gray-400'>None</span>
                          ) : (
                            <span className='rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700'>
                              {withDefault.length}
                            </span>
                          )}
                        </TableCell>
                      ),
                      actions: (
                        <TableCell className='text-right'>
                          <div className='flex items-center justify-end gap-3' onClick={(e) => e.stopPropagation()}>
                            <button
                              type='button'
                              className='flex items-center gap-1 text-sm font-medium text-primary hover:underline'
                              onClick={() => setModal({ profile: p })}
                            >
                              <Pencil className='h-3.5 w-3.5' />
                              Edit
                            </button>
                            {canConfigure && profiles.length > 1 && (
                              <button
                                type='button'
                                className='text-gray-300 hover:text-red-500'
                                title='Delete profile'
                                onClick={() => {
                                  if (window.confirm(`Delete profile "${p.name}"?`)) deleteProfile.mutate(p.id)
                                }}
                              >
                                <Trash2 className='h-4 w-4' />
                              </button>
                            )}
                          </div>
                        </TableCell>
                      ),
                    }}
                  />
                </TableRow>
                {isOpen && (
                  <TableRow className='hover:bg-transparent'>
                    <TableCell colSpan={cols.visible.length} className='bg-gray-50/60 p-0'>
                      <div className='px-5 py-4'>
                        <p className='mb-2.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400'>
                          Enabled fields · {yes.length}
                        </p>
                        {yes.length === 0 ? (
                          <p className='text-sm text-muted-foreground'>No fields enabled for this profile.</p>
                        ) : (
                          <div className='overflow-hidden rounded-lg border border-gray-200'>
                            <table className='w-full text-sm'>
                              <thead>
                                <tr className='bg-blue-50 text-left text-xs font-semibold uppercase tracking-wide text-blue-700'>
                                  {fieldCols.visible.map(({ def }) => (
                                    <ResizableTh
                                      key={def.id}
                                      columnId={def.id}
                                      columns={fieldCols}
                                      label={def.header}
                                      className='px-3 py-2'
                                    >
                                      {def.header}
                                    </ResizableTh>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {yes.map((f) => (
                                  <tr key={f.key} className='border-t border-gray-100 bg-white'>
                                    <ColumnCells
                                      columns={fieldCols}
                                      cells={{
                                        fieldName: <td className='px-3 py-1.5 font-medium text-gray-800'>{f.label}</td>,
                                        description: (
                                          <td className='px-3 py-1.5 text-gray-600'>
                                            {descByKey.get(f.key) || <span className='text-gray-300'>—</span>}
                                          </td>
                                        ),
                                        default: (
                                          <td className='px-3 py-1.5'>
                                            {hasDefault(f) ? (
                                              <span className='font-medium text-gray-800'>
                                                {f.dataType === 'enum'
                                                  ? conditionLabel(String(f.value))
                                                  : formatFieldValue(f.dataType, f.value)}
                                              </span>
                                            ) : (
                                              <span className='text-gray-300'>—</span>
                                            )}
                                          </td>
                                        ),
                                      }}
                                    />
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            )
          })}
        </TableBody>
      </Table>

      {profiles.length === 0 && (
        <p className='px-5 py-8 text-center text-sm text-muted-foreground'>No profiles yet — add one.</p>
      )}

      <ProfileFieldsModal
        profileType={profileType}
        profile={modal?.profile ?? null}
        open={modal !== null}
        onClose={() => setModal(null)}
      />
    </div>
  )
}
